import { loginReturningTo } from '../api/auth.js';
import { useAuthStatus } from '../hooks/useAuth.js';
import { useMyParty } from '../hooks/useMyParty.js';
import { matchRoute } from '../routing/match.js';
import { LOBBY_PATH, djPath, lobbyJoinPath, partySlugInPath, partyPath } from '../routing/paths.js';
import { useRoute } from '../routing/useRoute.js';

import { Avatar } from './Avatar.js';

import type { MyParty, PartySlug } from '@mixwire/contracts';
import type { ReactElement } from 'react';

type Destination = {
  readonly path: string;
  readonly label: string;
};

const destinationFor = (party: MyParty | null, path: string): Destination => {
  if (party === null) {
    return { path: LOBBY_PATH, label: 'Find a party' };
  }
  return path === djPath(party.slug, party.channelId)
    ? { path: partyPath(party.slug), label: 'Public view' }
    : { path: djPath(party.slug, party.channelId), label: 'Back to the decks' };
};

/**
 * Until the answer is in, the chip says nothing about a party rather than
 * asserting the DJ is in none — which is what it looked like it was saying.
 */
const partyLineOf = (party: MyParty | null | undefined): string | undefined => {
  if (party === undefined) {
    return undefined;
  }
  return party === null ? 'Not in a party' : `in ${party.displayName}`;
};

const SpotifyMark = (): ReactElement => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className="size-5 text-[#1DB954]">
    <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm4.586 14.424a.623.623 0 0 1-.857.207c-2.348-1.435-5.304-1.76-8.785-.964a.623.623 0 1 1-.277-1.215c3.809-.871 7.077-.496 9.712 1.115a.623.623 0 0 1 .207.857Zm1.223-2.722a.78.78 0 0 1-1.072.257c-2.688-1.652-6.786-2.131-9.965-1.166a.78.78 0 1 1-.452-1.492c3.632-1.102 8.147-.568 11.234 1.329a.78.78 0 0 1 .255 1.072Zm.105-2.835c-3.223-1.914-8.54-2.09-11.617-1.156a.935.935 0 1 1-.542-1.79c3.532-1.072 9.404-.865 13.115 1.338a.935.935 0 1 1-.956 1.608Z" />
  </svg>
);

/**
 * What an anonymous visitor on a party page gets instead: the way in. Signing
 * in from here lands on the lobby already opened on the party they were
 * watching, which is the party they almost certainly want to play at.
 */
const SignInChip = ({ slug }: { readonly slug: PartySlug }): ReactElement => (
  <a
    href={loginReturningTo(lobbyJoinPath(slug))}
    title="Log in to DJ at this party"
    className="bg-surface border-subtle text-muted hover:text-content hover:bg-surface-hover group flex shrink-0 items-center gap-2 rounded-pill border px-3 py-2 transition-colors"
  >
    <SpotifyMark />
    <span className="hidden text-sm font-medium group-hover:inline group-focus-visible:inline sm:inline">
      Log in to DJ here
    </span>
  </a>
);

/**
 * The signed-in DJ's own marker, on every page including the public view: it
 * says who this browser is and which party it holds a channel at, and it is
 * the way back to the decks. Absent entirely for anyone not signed in, so a
 * projector or a suggester's phone shows nothing at all.
 */
export const IdentityChip = (): ReactElement | null => {
  const status = useAuthStatus();
  const { path, navigate } = useRoute();
  // The console carries the DJ's identity in its own header, where it fits the
  // coloured band instead of hanging over it.
  const isOwnConsole = matchRoute('/:slug/dj/:channelId', path) !== undefined;
  const isAuthenticated = status.data?.authenticated === true;
  const myParty = useMyParty(isAuthenticated);

  if (isOwnConsole || status.data === undefined) {
    return null;
  }

  if (!status.data.authenticated) {
    const viewedSlug = partySlugInPath(path);
    return viewedSlug === undefined ? null : <SignInChip slug={viewedSlug} />;
  }

  const { user } = status.data;
  const party = myParty.data?.party ?? null;
  const destination = destinationFor(party, path);
  const partyLine = partyLineOf(myParty.data?.party);

  return (
    <a
      href={destination.path}
      onClick={(event) => {
        event.preventDefault();
        navigate(destination.path);
      }}
      className="border-subtle hover:bg-surface-hover group flex max-w-80 shrink-0 items-center gap-2.5 rounded-pill border p-1 transition-colors"
    >
      <Avatar displayName={user.displayName} avatarUrl={user.avatarUrl} />
      <span className="hidden min-w-0 flex-col pr-2 sm:flex">
        <span className="text-content truncate text-sm font-medium group-hover:hidden group-focus-visible:hidden">
          {user.displayName}
        </span>
        <span className="text-accent hidden truncate text-sm font-semibold group-hover:inline group-focus-visible:inline">
          {destination.label}
        </span>
        {partyLine === undefined ? null : (
          <span className="text-muted truncate text-xs">{partyLine}</span>
        )}
      </span>
    </a>
  );
};
